import { useCallback, useEffect, useMemo, useState } from "react";
import { useTrips, type TripWithId } from "./useTrips";
import { useRecentInteractions } from "./useRecentInteractions";
import { isActivityActive } from "../utils/tripDisplay";
import { dayKeyOf, dayRangeContains, todayKey } from "../utils/calendarDay";

const FOCUSED_TRIP_KEY = "triptracker.focusedTripId";

function readStoredFocus(): string | null {
  try {
    return localStorage.getItem(FOCUSED_TRIP_KEY);
  } catch {
    return null;
  }
}

function writeStoredFocus(id: string | null) {
  try {
    if (id) localStorage.setItem(FOCUSED_TRIP_KEY, id);
    else localStorage.removeItem(FOCUSED_TRIP_KEY);
  } catch {
    /* ignore unavailable storage */
  }
}

/**
 * Determines which trips are "current" using a hybrid rule: a trip counts if
 * today falls within its dates OR it has an interaction that's active right
 * now. Results are ranked most-recently-touched first. The user can pin one
 * of the current trips as their "focused" trip; the pin is remembered in
 * localStorage and dropped automatically once that trip is no longer current.
 */
export function useCurrentTrips() {
  const { trips, loading: tripsLoading } = useTrips();
  const { interactions: recent, loading: recentLoading } = useRecentInteractions();

  const activeTripIds = useMemo(() => {
    const ids = new Set<string>();
    for (const i of recent) {
      if (isActivityActive(i.events, undefined, i.customResponses as Record<string, unknown> | undefined)) ids.add(i.tripId);
    }
    return ids;
  }, [recent]);

  const lastActivityAt = useMemo(() => {
    const map = new Map<string, number>();
    for (const i of recent) {
      const t = i.createdAt?.toDate?.().getTime() ?? 0;
      if (t > (map.get(i.tripId) ?? 0)) map.set(i.tripId, t);
    }
    return map;
  }, [recent]);

  const currentTrips = useMemo<TripWithId[]>(() => {
    const today = todayKey();
    const matches = trips.filter((t) => {
      const withinDates = dayRangeContains(t.startDate, t.endDate, today);
      return withinDates || activeTripIds.has(t.id);
    });
    return matches.sort((a, b) => {
      const la = lastActivityAt.get(a.id) ?? 0;
      const lb = lastActivityAt.get(b.id) ?? 0;
      if (lb !== la) return lb - la;
      return (b.startDate?.toDate?.().getTime() ?? 0) - (a.startDate?.toDate?.().getTime() ?? 0);
    });
  }, [trips, activeTripIds, lastActivityAt]);

  const upcomingTrip = useMemo<TripWithId | null>(() => {
    const today = todayKey();
    return (
      [...trips]
        .filter((t) => {
          const start = dayKeyOf(t.startDate);
          return !!start && start > today;
        })
        .sort(
          (a, b) =>
            (a.startDate?.toDate?.().getTime() ?? 0) - (b.startDate?.toDate?.().getTime() ?? 0),
        )[0] ?? null
    );
  }, [trips]);

  const [pinnedTripId, setPinnedTripId] = useState<string | null>(readStoredFocus);

  // Once data has settled, drop a stale pin that no longer points at a current trip.
  useEffect(() => {
    if (tripsLoading || recentLoading) return;
    if (pinnedTripId && !currentTrips.some((t) => t.id === pinnedTripId)) {
      writeStoredFocus(null);
      setPinnedTripId(null);
    }
  }, [pinnedTripId, currentTrips, tripsLoading, recentLoading]);

  const setFocusedTrip = useCallback((id: string | null) => {
    writeStoredFocus(id);
    setPinnedTripId(id);
  }, []);

  const focusedTripId =
    pinnedTripId && currentTrips.some((t) => t.id === pinnedTripId)
      ? pinnedTripId
      : currentTrips[0]?.id ?? null;

  const focusedTrip = useMemo<TripWithId | null>(
    () => currentTrips.find((t) => t.id === focusedTripId) ?? null,
    [currentTrips, focusedTripId],
  );

  return {
    currentTrips,
    focusedTrip,
    focusedTripId,
    setFocusedTrip,
    upcomingTrip,
    allTrips: trips,
    loading: tripsLoading,
  };
}
